import SectionCard from './SectionCard';
import ToggleField from './ToggleField';
import Segmented from '@/components/base/Segmented';
import Toggle from '@/components/base/Toggle';
import {
  type SalesSettings,
  type DiscountRole,
  formatMoney,
} from '@/lib/salesSettings';

interface SectionProps {
  settings: SalesSettings;
  patch: (partial: Partial<SalesSettings>) => void;
}

export function MonedaSection({ settings }: SectionProps) {
  return (
    <SectionCard
      icon="ri-money-dollar-circle-line"
      title="Moneda"
      description="Moneda que se utilizará en todos los precios y totales del punto de venta."
    >
      <div>
        <label className="block text-sm font-medium text-foreground-800 mb-1.5">
          Moneda principal
        </label>
        <div className="flex items-center gap-3 rounded-md border border-background-300 bg-background-100 px-4 py-2.5">
          <span className="text-sm font-medium text-foreground-900">
            {settings.currency_name} ({settings.currency_symbol})
          </span>
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-lock-line" /> No editable
          </span>
        </div>
      </div>

      <div className="rounded-md bg-accent-50 border border-accent-200 px-4 py-3">
        <p className="text-xs font-medium text-foreground-500 mb-1">Ejemplo de formato</p>
        <p className="font-heading text-lg font-semibold text-foreground-950">
          {formatMoney(settings, 1250)}
        </p>
      </div>
    </SectionCard>
  );
}

export function ImpuestosSection({ settings, patch }: SectionProps) {
  return (
    <SectionCard
      icon="ri-percent-line"
      title="Impuestos"
      description="Configura si las ventas aplican impuestos y su porcentaje."
    >
      <ToggleField
        label="¿Aplicar impuestos a las ventas?"
        description="Si está desactivado, el punto de venta no agregará impuestos a las ventas."
        checked={settings.taxes_enabled}
        onChange={(v) => patch({ taxes_enabled: v })}
      />

      <div
        className={`transition-opacity duration-200 ${
          settings.taxes_enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'
        }`}
      >
        <label className="block text-sm font-medium text-foreground-800 mb-1.5">
          Porcentaje de impuesto
        </label>
        <div className="relative max-w-[180px]">
          <input
            type="number"
            min={0}
            max={100}
            step={0.01}
            value={settings.tax_percentage}
            onChange={(e) =>
              patch({ tax_percentage: e.target.value === '' ? 0 : Number(e.target.value) })
            }
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 pr-9 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500">
            %
          </span>
        </div>
        {settings.taxes_enabled && (
          <p className="mt-2 text-xs text-foreground-500">
            Ejemplo: un producto de {formatMoney(settings, 1000)} pasará a{' '}
            {formatMoney(settings, 1000 * (1 + settings.tax_percentage / 100))}.
          </p>
        )}
      </div>
    </SectionCard>
  );
}

export function DescuentosSection({ settings, patch }: SectionProps) {
  return (
    <SectionCard
      icon="ri-discount-percent-line"
      title="Descuentos"
      description="Configura si se permiten descuentos y quién puede aplicarlos."
    >
      <ToggleField
        label="¿Permitir descuentos en ventas?"
        checked={settings.discounts_enabled}
        onChange={(v) => patch({ discounts_enabled: v })}
      />

      <div
        className={`space-y-5 transition-opacity duration-200 ${
          settings.discounts_enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'
        }`}
      >
        <div>
          <p className="text-sm font-medium text-foreground-900 mb-2.5">Tipos de descuento permitidos</p>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-foreground-700">Porcentaje</span>
              <Toggle
                checked={settings.discount_percentage_allowed}
                onChange={(v) => patch({ discount_percentage_allowed: v })}
                label="Descuento por porcentaje"
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-foreground-700">Monto fijo</span>
              <Toggle
                checked={settings.discount_fixed_allowed}
                onChange={(v) => patch({ discount_fixed_allowed: v })}
                label="Descuento por monto fijo"
              />
            </div>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-foreground-900 mb-2.5">
            ¿Quién puede aplicar descuentos?
          </p>
          <Segmented
            value={settings.discount_allowed_roles}
            onChange={(v) => patch({ discount_allowed_roles: v as DiscountRole })}
            options={[
              { value: 'admin', label: 'Administrador' },
              { value: 'admin_cashier', label: 'Administrador y cajero' },
            ]}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1.5">
            Porcentaje máximo sin autorización
          </label>
          <div className="relative max-w-[180px]">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={settings.discount_max_percentage}
              onChange={(e) =>
                patch({
                  discount_max_percentage: e.target.value === '' ? 0 : Number(e.target.value),
                })
              }
              className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 pr-9 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500">
              %
            </span>
          </div>
          <p className="mt-2 text-xs text-foreground-500">
            {settings.discount_max_percentage > 0
              ? `El cajero podrá aplicar hasta ${settings.discount_max_percentage}% sin autorización. Por encima de ese valor se pedirá la aprobación de un administrador.`
              : 'En 0 no hay límite: el cajero puede aplicar cualquier porcentaje sin autorización.'}
          </p>
        </div>
      </div>
    </SectionCard>
  );
}

export function MetodosPagoSection({ settings, patch }: SectionProps) {
  const activeCount = settings.payment_methods.filter((m) => m.enabled).length;

  return (
    <SectionCard
      icon="ri-bank-card-line"
      title="Métodos de pago"
      description="Activa o desactiva los métodos de pago disponibles en caja. El pago mixto combina efectivo y tarjeta en una misma venta."
    >
      <div className="divide-y divide-background-100">
        {settings.payment_methods.map((method) => (
          <div key={method.id} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between gap-4">
            <span className="text-sm font-medium text-foreground-900">{method.name}</span>
            <Toggle
              checked={method.enabled}
              onChange={(v) =>
                patch({
                  payment_methods: settings.payment_methods.map((m) =>
                    m.id === method.id ? { ...m, enabled: v } : m
                  ),
                })
              }
              label={`Método ${method.name}`}
            />
          </div>
        ))}
      </div>

      {activeCount === 0 ? (
        <p className="rounded-md bg-primary-50 border border-primary-200 px-3 py-2 text-sm text-primary-800">
          Debe haber al menos un método de pago activo.
        </p>
      ) : (
        <p className="text-xs text-foreground-500">
          {activeCount} de {settings.payment_methods.length} métodos activos.
        </p>
      )}
    </SectionCard>
  );
}

export function EfectivoSection({ settings, patch }: SectionProps) {
  return (
    <SectionCard
      icon="ri-cash-line"
      title="Configuración de efectivo"
      description="Define cómo se manejará el efectivo al cobrar en caja."
    >
      <ToggleField
        label="¿Solicitar monto recibido?"
        description="El cajero deberá ingresar el monto recibido para calcular el cambio."
        checked={settings.cash_require_received}
        onChange={(v) => patch({ cash_require_received: v })}
      />

      <div className="rounded-md bg-background-100 border border-background-200 px-4 py-3">
        <p className="text-xs font-medium text-foreground-500 mb-3">Vista previa (ejemplo)</p>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-foreground-600">Total</span>
            <span className="font-medium text-foreground-950">{formatMoney(settings, 850)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-600">Recibido</span>
            <span className="font-medium text-foreground-950">{formatMoney(settings, 1000)}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-background-200">
            <span className="text-foreground-600">Cambio</span>
            <span className="font-semibold text-accent-700">{formatMoney(settings, 150)}</span>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}